import React, { FC, useState } from "react";
import { Outlet } from "react-router-dom";
import { Box, styled, useMediaQuery } from "@mui/material";
import DashboardNavbar from "./DashboardNavbar";
import DashboardSidebar from "./sidemenu";
import VersionUpdate from "../UI/VersionUpdate";
import { ModalProvider } from "../../contexts/ModalDialogContext";
import { DrawerProvider } from "../../contexts/DrawerContext";
import { TaskIndicatorProvider } from "../../contexts/TaskIndicatorContext";
import { ConsentProvider } from "../../contexts/ConsentBoxContext";
import DashboardHeader from "./sidemenu/DashboardHeader";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import RightSideBar from "./sidemenu/RightSideBar";

const Wrapper = styled(Box)(({ theme }) => ({
  maxWidth: `calc(100%)`,
  marginLeft: "20%",
  marginRight: 0,
  paddingLeft: "0.1rem",
  paddingRight: "0rem",
  overflowY: "scroll",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const MainContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
}));

const StyledSideNavWrapper = styled(Box)(({ theme }) => ({
  width: "6%",
  background: "#fff",
  borderLeft: "1px solid #D9D9D9",
  [theme.breakpoints.down("md")]: {
    position: "absolute",
    width: "100%",
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    marginTop: "auto",
    display: "flex",
    flexDirection: "row",
    zIndex: 1000,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "94%",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    paddingBottom: 80,
  },
}));

const RouterOutlet: any = Outlet;

const DashboardLayout: FC<any> = ({ children }) => {
  const theme = useTheme();
  const layoutRef = React.useRef<HTMLDivElement>(null);

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
    React.useState(true);
  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
    React.useState(false);

  const isOverMdViewport = useMediaQuery(theme.breakpoints.up("md"));

  const isNavigationExpanded = isOverMdViewport
    ? isDesktopNavigationExpanded
    : isMobileNavigationExpanded;

  const setIsNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => {
      if (isOverMdViewport) {
        setIsDesktopNavigationExpanded(newExpanded);
      } else {
        setIsMobileNavigationExpanded(newExpanded);
      }
    },
    [
      isOverMdViewport,
      setIsDesktopNavigationExpanded,
      setIsMobileNavigationExpanded,
    ],
  );

  const handleToggleHeaderMenu = React.useCallback(
    (isExpanded: boolean) => {
      setIsNavigationExpanded(isExpanded);
    },
    [setIsNavigationExpanded],
  );
  const [showMobileSideBar, setShowMobileSideBar] = useState(false);

  return (
    <>
      <DrawerProvider>
        <TaskIndicatorProvider>
          <ModalProvider>
            <ConsentProvider>
              <Box
                ref={layoutRef}
                sx={{
                  position: "relative",
                  display: "flex",
                  overflow: "hidden",
                  height: "100%",
                  width: "100%",
                }}
              >
                <DashboardHeader
                  menuOpen={isNavigationExpanded}
                  onToggleMenu={handleToggleHeaderMenu}
                  setShowMobileSideBar={() =>
                    setShowMobileSideBar((state) => !state)
                  }
                />
                <DashboardSidebar
                  showMobileSideBar={showMobileSideBar}
                  closeMobileSideBar={() => setShowMobileSideBar(false)}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Toolbar sx={{ displayPrint: "none" }} />
                  <Wrapper>
                    <MainContentWrapper>
                      <ContentWrapper>
                        {children || <RouterOutlet />}
                      </ContentWrapper>
                      <StyledSideNavWrapper>
                        <RightSideBar />
                      </StyledSideNavWrapper>
                    </MainContentWrapper>
                  </Wrapper>
                </Box>
              </Box>
            </ConsentProvider>
          </ModalProvider>
          <VersionUpdate />
        </TaskIndicatorProvider>
      </DrawerProvider>
    </>
  );
};

export default DashboardLayout;
